// ============================================================================
// LANDING PAGE - Modern Light Theme with Animations
// ALL TEXT USES TRANSLATION KEYS
// ============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/AuthContext';
import { 
  ArrowRight, CheckCircle, TrendingUp, 
  BarChart3, FileText, User, Building2,
  ChevronDown, Shield, Lock, RefreshCw
} from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Header variant="light" showNav={true} />

      {/* HERO SECTION */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-teal-100/50 via-cyan-50/30 to-transparent rounded-full blur-3xl"
            style={{ animation: 'pulse 4s ease-in-out infinite' }}
          />
          <div 
            className="absolute top-20 right-1/4 w-[300px] h-[300px] bg-gradient-to-br from-teal-200/30 to-cyan-100/20 rounded-full blur-2xl"
            style={{ animation: 'float 6s ease-in-out infinite' }}
          />
          <div 
            className="absolute top-40 left-1/4 w-[200px] h-[200px] bg-gradient-to-br from-cyan-200/20 to-teal-100/10 rounded-full blur-2xl"
            style={{ animation: 'float 8s ease-in-out infinite 1s' }}
          />
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] mb-6 tracking-tight">
            <span 
              className="inline-block"
              style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}
            >
              {t('landing.hero.title1')}
            </span>
            <br />
            <span 
              className="inline-block bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500 bg-clip-text text-transparent"
              style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}
            >
              {t('landing.hero.title2')}
            </span>
          </h1>

          <p 
            className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.5s both' }}
          >
            {t('landing.hero.subtitle')}
          </p>

          <div style={{ animation: 'fadeInUp 0.6s ease-out 0.7s both' }}>
            <Link
              to="/assessment"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 hover:scale-105 transition-all duration-300 shadow-lg shadow-gray-900/20 text-lg group"
            >
              {t('cta.takeAssessment')}
              <ArrowRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR PATH */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {t('landing.paths.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              {t('landing.paths.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            
            {/* Personal Card */}
            <div className="group relative bg-white rounded-3xl p-8 border border-gray-200/80 hover:border-teal-300 transition-all duration-500 hover:shadow-2xl hover:shadow-teal-500/10 hover:-translate-y-1">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-teal-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-cyan-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-teal-200/50">
                  <User className="w-7 h-7 text-teal-600" />
                </div>

                <div className="mb-6">
                  <span className="text-xs font-semibold text-teal-600 uppercase tracking-wider">
                    {t('landing.paths.personal.label')}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mt-2 mb-3">
                    {t('landing.paths.personal.title')}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('landing.paths.personal.desc')}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-600 text-sm">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    {t('landing.paths.personal.feature1')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-600 text-sm">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    {t('landing.paths.personal.feature2')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-600 text-sm">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    {t('landing.fiveMinutes')}
                  </li>
                </ul>

                <Link
                  to="/assessment"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 group/btn"
                >
                  {t('cta.takeAssessment')}
                  <ArrowRight className={`w-5 h-5 group-hover/btn:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
              </div>
            </div>

            {/* Business Card */}
            <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 border border-gray-700 hover:border-cyan-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-1">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-white/10">
                  <Building2 className="w-7 h-7 text-cyan-400" />
                </div>

                <div className="mb-6">
                  <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">
                    {t('landing.paths.business.label')}
                  </span>
                  <h3 className="text-2xl font-bold text-white mt-2 mb-3">
                    {t('landing.paths.business.title')}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {t('landing.paths.business.desc')}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-400 text-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    {t('landing.paths.business.feature1')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-400 text-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    {t('landing.paths.business.feature2')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-400 text-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    {t('landing.paths.business.feature3')}
                  </li>
                </ul>

                <a
                  href="https://business.lab2040.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-full hover:from-teal-400 hover:to-cyan-400 transition-all duration-300 group/btn"
                >
                  {t('landing.paths.business.cta')}
                  <ArrowRight className={`w-5 h-5 group-hover/btn:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.howItWorks')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.researchBased')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: FileText,
                titleKey: 'landing.features.assess.title',
                descKey: 'landing.features.assess.desc',
                color: 'teal'
              },
              {
                step: '02',
                icon: BarChart3,
                titleKey: 'landing.features.score.title',
                descKey: 'landing.features.score.desc',
                color: 'cyan'
              },
              {
                step: '03',
                icon: TrendingUp,
                titleKey: 'landing.features.roadmap.title',
                descKey: 'landing.features.roadmap.desc',
                color: 'teal'
              }
            ].map((item, i) => (
              <div key={i} className="group relative">
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 h-full">
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${item.color === 'teal' ? 'bg-teal-100 text-teal-600' : 'bg-cyan-100 text-cyan-600'} text-sm font-bold mb-6`}>
                    {item.step}
                  </div>
                  
                  <div className={`w-12 h-12 ${item.color === 'teal' ? 'bg-teal-100' : 'bg-cyan-100'} rounded-xl flex items-center justify-center mb-6`}>
                    <item.icon className={`w-6 h-6 ${item.color === 'teal' ? 'text-teal-600' : 'text-cyan-600'}`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-gray-600">
                    {t(item.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.trust.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.trust.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                titleKey: 'landing.trust.methodology.title',
                descKey: 'landing.trust.methodology.desc'
              },
              {
                icon: Lock,
                titleKey: 'landing.trust.privacy.title',
                descKey: 'landing.trust.privacy.desc'
              },
              {
                icon: RefreshCw,
                titleKey: 'landing.trust.updates.title',
                descKey: 'landing.trust.updates.desc'
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t(item.titleKey)}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.faq.title')}
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { q: t('landing.faq.q1'), a: t('landing.faq.a1') },
              { q: t('landing.faq.q2'), a: t('landing.faq.a2') },
              { q: t('landing.faq.q3'), a: t('landing.faq.a3') },
              { q: t('landing.faq.q4'), a: t('landing.faq.a4') },
              { q: t('landing.faq.q5'), a: t('landing.faq.a5') }
            ].map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 hover:bg-gray-100/50 transition-colors">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-semibold text-gray-900 pe-4">{faq.q}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === i ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-to-t from-teal-500/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg text-gray-400 mb-4 max-w-2xl mx-auto">
            {t('landing.cta.subtitle')}
          </p>
          <p className="text-base text-gray-500 mb-10 max-w-2xl mx-auto italic">
            {t('landing.cta.statement')}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/assessment"
              className="flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-full hover:bg-gray-100 hover:scale-105 transition-all duration-300"
            >
              {t('cta.takeAssessmentNow')}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
            <a
              href="https://business.lab2040.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-4 bg-transparent border border-white/20 text-white font-bold rounded-full hover:bg-white/10 hover:border-white/30 transition-all duration-300"
            >
              {t('landing.paths.business.cta')}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </a>
          </div>
        </div>
      </section>

      <Footer variant="light" />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default Landing;
